import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { RuntimeEntityGroupConfig } from 'src/app/services/template-mappers/base-template-mapper';
import { detailExpandAnimation } from 'src/app/animations/detailExpand.animation';

export enum PaletteFieldGroup {
    Bound = 'bound',
    Runtime = 'runtime',
    Custom = 'custom',
    Context = 'context',
}

export interface PaletteField {
    path: string;
    label: string;
    group: PaletteFieldGroup;
    hidden?: boolean;
    entityGroup?: string;
}

export interface RuntimeEntityGroup {
  key: string;
  label: string;
  fields: PaletteField[];
  expanded: boolean;
  accent: string;
}

@Component({
    standalone: true,
    selector: 'app-placeholder-palette',
    imports: [FormsModule, SharedModule],
    templateUrl: './placeholder-palette.component.html',
    styleUrls: ['./placeholder-palette.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [detailExpandAnimation]
})
export class PlaceholderPaletteComponent implements OnInit, OnChanges {
  @Input() fields: PaletteField[] = [];
  @Input() runtimeGroups: readonly RuntimeEntityGroupConfig[] = [];
  @Input() contextFields: PaletteField[] = [];
  @Input() contextLabel = '';
  @Input() hasLoopScopes = false;
  @Output() fieldInserted = new EventEmitter<string>();

  searchTerm = '';
  filteredFields: PaletteField[] = [];
  boundFieldsView: PaletteField[] = [];
  contextFieldsView: PaletteField[] = [];
  runtimeEntityGroups: RuntimeEntityGroup[] = [];
  expandedEntities: Set<string> = new Set();

  ngOnInit(): void {
    this.expandedEntities = new Set(this.runtimeGroups.map(g => g.key));
    this.applyFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['runtimeGroups'] || changes['contextFields']) {
      if (changes['runtimeGroups'] && this.expandedEntities.size === 0) {
        this.expandedEntities = new Set(this.runtimeGroups.map(g => g.key));
      }
      this.applyFilter();
    }
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  insertField(path: string): void {
    this.fieldInserted.emit(path);
  }

  toggleEntity(key: string): void {
    if (this.expandedEntities.has(key)) {
      this.expandedEntities.delete(key);
    } else {
      this.expandedEntities.add(key);
    }
    this.applyFilter();
  }

  private applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    const visible = this.fields.filter(f => !f.hidden);
    const matched = term
      ? visible.filter(f => f.path.toLowerCase().includes(term) || f.label.toLowerCase().includes(term))
      : [...visible];

    this.contextFieldsView = term
      ? this.contextFields.filter(f => f.path.toLowerCase().includes(term) || f.label.toLowerCase().includes(term))
      : [...this.contextFields];

    this.boundFieldsView = matched.filter(f => f.group === PaletteFieldGroup.Bound);

    const groups: RuntimeEntityGroup[] = this.runtimeGroups
      .map(rg => ({
        key: rg.key,
        label: rg.label,
        fields: matched.filter(f => f.group === PaletteFieldGroup.Runtime && f.entityGroup === rg.key),
        accent: rg.accent,
        expanded: term ? true : this.expandedEntities.has(rg.key),
      }))
      .filter(g => g.fields.length > 0)
      .sort((a, b) => b.fields.length - a.fields.length);

    this.runtimeEntityGroups = groups;

    this.filteredFields = [...this.contextFieldsView, ...this.boundFieldsView, ...groups.flatMap(g => g.fields)];
  }

  trackByPath(_: number, field: PaletteField): string {
    return field.path;
  }

  trackByEntity(_: number, group: RuntimeEntityGroup): string {
    return group.key;
  }
}
